(() => {
  'use strict';
  const SELECTOR = '[data-tpdp-root]';
  const initTabs = (root) => {
    const tabs = root.querySelector('[data-tpdp-tabs]');
    if (!tabs) return;
    const links = Array.from(tabs.querySelectorAll('[data-tpdp-tab]'));
    const sections = Array.from(tabs.querySelectorAll('[data-tpdp-section]'));
    const list = tabs.querySelector('.tpdp-tabs__list');
    if (!links.length || !sections.length) return;
    const keepActiveLinkVisible = (link) => {
      if (!list || !link) return;
      const listRect = list.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      let delta = 0;
      if (linkRect.left < listRect.left) {
        delta = linkRect.left - listRect.left;
      } else if (linkRect.right > listRect.right) {
        delta = linkRect.right - listRect.right;
      }
      if (Math.abs(delta) > 1) {
        list.scrollBy({ left: delta, behavior: 'smooth' });
      }
    };
    const setActiveSection = (sectionId) => {
      links.forEach((link) => {
        const active = link.dataset.tpdpTab === sectionId;
        link.classList.toggle('is-active', active);
        if (active) {
          link.setAttribute('aria-current', 'true');
          keepActiveLinkVisible(link);
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };
    const getScrollOffset = () => {
      if (!list) return 16;
      const listStyles = window.getComputedStyle(list);
      const isSticky = listStyles.position === 'sticky';
      const stickyHeight = isSticky ? list.getBoundingClientRect().height : 0;
      return stickyHeight + 16;
    };
    const scrollToSection = (sectionId, focusLink = false) => {
      const section = root.querySelector(`#${CSS.escape(sectionId)}`);
      const link = links.find((item) => item.dataset.tpdpTab === sectionId);
      if (!section) return;
      setActiveSection(sectionId);
      const top = section.getBoundingClientRect().top + window.scrollY - getScrollOffset();
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      if (focusLink) link?.focus();
    };
    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-tpdp-tab]');
      if (link && tabs.contains(link)) {
        event.preventDefault();
        scrollToSection(link.dataset.tpdpTab);
        return;
      }
      const opener = event.target.closest('[data-tpdp-open-tab]');
      if (opener && root.contains(opener)) {
        event.preventDefault();
        scrollToSection(opener.dataset.tpdpOpenTab);
      }
    });
    root.addEventListener('keydown', (event) => {
      const current = event.target.closest('[data-tpdp-tab]');
      if (!current || !tabs.contains(current)) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const currentIndex = links.indexOf(current);
      if (currentIndex < 0) return;
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % links.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + links.length) % links.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = links.length - 1;
      event.preventDefault();
      scrollToSection(links[nextIndex].dataset.tpdpTab, true);
    });
    let scrollFrame = 0;
    const updateActiveFromScroll = () => {
      scrollFrame = 0;
      const trigger = getScrollOffset() + 12;
      let activeSection = sections[0];
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= trigger) {
          activeSection = section;
        } else {
          break;
        }
      }
      const pageBottom = window.innerHeight + window.scrollY;
      const documentBottom = document.documentElement.scrollHeight;
      if (pageBottom >= documentBottom - 2) {
        activeSection = sections[sections.length - 1];
      }
      setActiveSection(activeSection.id);
    };
    const scheduleActiveUpdate = () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(updateActiveFromScroll);
    };
    window.addEventListener('scroll', scheduleActiveUpdate, { passive: true });
    window.addEventListener('resize', scheduleActiveUpdate, { passive: true });
    requestAnimationFrame(() => {
      const hashId = window.location.hash ? window.location.hash.slice(1) : '';
      if (hashId && sections.some((section) => section.id === hashId)) {
        setActiveSection(hashId);
      } else {
        updateActiveFromScroll();
      }
    });
  };
  const initQuantity = (root) => {
    const field = root.querySelector('.tpdp-qty select');
    if (!field) return;
    field.addEventListener('change', () => {
      const value = Number.parseInt(field.value, 10);
      field.value = String(Number.isFinite(value) && value > 0 ? value : 1);
    });
  };
  const initCart = (root) => {
    const form = root.querySelector('[data-tpdp-form]');
    const button = root.querySelector('[data-tpdp-add]');
    const message = root.querySelector('[data-tpdp-form-message]');
    if (!form || !button || !message) return;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (button.disabled) return;
      button.disabled = true;
      message.classList.remove('is-error');
      message.textContent = '';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) {
          message.classList.add('is-error');
          message.textContent = 'Não foi possível adicionar a peça ao carrinho.';
          return;
        }
        const item = await response.json();
        message.textContent = 'Peça adicionada ao carrinho.';
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            source: 'tomauto-pdp-v1',
            item
          }
        }));
      } catch {
        message.classList.add('is-error');
        message.textContent = 'Não foi possível adicionar a peça ao carrinho.';
      } finally {
        button.disabled = false;
      }
    });
  };
  const initGallery = (root) => {
    const panels = Array.from(root.querySelectorAll('[data-tpdp-media-panel]'));
    const thumbs = Array.from(root.querySelectorAll('[data-tpdp-thumb]'));
    const thumbTrack = root.querySelector('[data-tpdp-thumbs-track]');
    const thumbPrev = root.querySelector('[data-tpdp-thumbs-prev]');
    const thumbNext = root.querySelector('[data-tpdp-thumbs-next]');
    if (!panels.length) return;
    let currentIndex = Math.max(0, panels.findIndex((panel) => panel.classList.contains('is-active')));
    let thumbScrollFrame = 0;
    const getThumbGap = () => {
      if (!thumbTrack) return 0;
      const styles = window.getComputedStyle(thumbTrack);
      return Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    };
    const getThumbStep = () => {
      const firstThumb = thumbs[0];
      if (!firstThumb) return thumbTrack?.clientWidth || 0;
      return firstThumb.getBoundingClientRect().width + getThumbGap();
    };
    const getThumbMaxScroll = () => {
      if (!thumbTrack) return 0;
      return Math.max(0, thumbTrack.scrollWidth - thumbTrack.clientWidth);
    };
    const updateThumbControls = () => {
      if (!thumbTrack || !thumbPrev || !thumbNext) return;
      const maxScroll = getThumbMaxScroll();
      const hasOverflow = maxScroll > 1;
      const atStart = thumbTrack.scrollLeft <= 1;
      const atEnd = thumbTrack.scrollLeft >= maxScroll - 1;
      thumbPrev.disabled = !hasOverflow || atStart;
      thumbNext.disabled = !hasOverflow || atEnd;
      thumbPrev.setAttribute('aria-disabled', String(thumbPrev.disabled));
      thumbNext.setAttribute('aria-disabled', String(thumbNext.disabled));
    };
    const scheduleThumbControlsUpdate = () => {
      if (thumbScrollFrame) cancelAnimationFrame(thumbScrollFrame);
      thumbScrollFrame = requestAnimationFrame(() => {
        thumbScrollFrame = 0;
        updateThumbControls();
      });
    };
    const ensureThumbVisible = (index, behavior = 'smooth') => {
      if (!thumbTrack) return;
      const thumb = thumbs[index];
      if (!thumb) return;
      const trackRect = thumbTrack.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      let delta = 0;
      if (thumbRect.left < trackRect.left) {
        delta = thumbRect.left - trackRect.left;
      } else if (thumbRect.right > trackRect.right) {
        delta = thumbRect.right - trackRect.right;
      }
      if (Math.abs(delta) > 1) {
        thumbTrack.scrollBy({ left: delta, behavior });
      }
    };
    const show = (index) => {
      currentIndex = (index + panels.length) % panels.length;
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === currentIndex;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === currentIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });
      ensureThumbVisible(currentIndex);
    };
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number.parseInt(thumb.dataset.tpdpThumb, 10);
        if (Number.isFinite(index)) show(index);
      });
    });
    if (thumbTrack && thumbPrev && thumbNext) {
      thumbPrev.addEventListener('click', () => {
        if (thumbPrev.disabled) return;
        thumbTrack.scrollBy({
          left: -getThumbStep(),
          behavior: 'smooth'
        });
      });
      thumbNext.addEventListener('click', () => {
        if (thumbNext.disabled) return;
        thumbTrack.scrollBy({
          left: getThumbStep(),
          behavior: 'smooth'
        });
      });
      thumbTrack.addEventListener('scroll', scheduleThumbControlsUpdate, { passive: true });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(scheduleThumbControlsUpdate);
        observer.observe(thumbTrack);
      } else {
        window.addEventListener('resize', scheduleThumbControlsUpdate, { passive: true });
      }
      requestAnimationFrame(updateThumbControls);
    }
    root.querySelector('[data-tpdp-gallery-prev]')?.addEventListener('click', () => show(currentIndex - 1));
    root.querySelector('[data-tpdp-gallery-next]')?.addEventListener('click', () => show(currentIndex + 1));
  };
  const initTechRows = (root) => {
    const rows = Array.from(root.querySelectorAll('[data-tpdp-tech-row]'));
    if (!rows.length) return;
    const techLayout = root.querySelector('.tpdp-tech__layout');
    const showMoreButton = root.querySelector('[data-tpdp-tech-show-more]');
    const rowLimit = 8;
    const hasExtraRows = rows.length > rowLimit;
    if (techLayout && hasExtraRows) {
      techLayout.classList.add('is-limited');
    }
    const getCollapsedHeight = (value) => {
      const styles = window.getComputedStyle(value);
      const fontSize = Number.parseFloat(styles.fontSize) || 13;
      const parsedLineHeight = Number.parseFloat(styles.lineHeight);
      const lineHeight = Number.isFinite(parsedLineHeight)
        ? parsedLineHeight
        : fontSize * 1.35;
      return Math.ceil(lineHeight);
    };
    const configureRow = (row) => {
      const value = row.querySelector('[data-tpdp-tech-value]');
      const toggle = row.querySelector('[data-tpdp-tech-toggle]');
      if (!value || !toggle) return;
      const wasExpanded = row.classList.contains('is-expanded');
      row.classList.remove('is-collapsible', 'is-expanded');
      value.style.removeProperty('--tpdp-tech-collapsed-height');
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      const collapsedHeight = getCollapsedHeight(value);
      const fullHeight = Math.ceil(value.scrollHeight);
      if (fullHeight > collapsedHeight + 1) {
        row.classList.add('is-collapsible');
        value.style.setProperty('--tpdp-tech-collapsed-height', `${collapsedHeight}px`);
        toggle.hidden = false;
        if (wasExpanded) {
          row.classList.add('is-expanded');
          toggle.setAttribute('aria-expanded', 'true');
        }
      }
    };
    const configureAll = () => {
      rows.forEach(configureRow);
    };
    const expandAllTechRows = (button) => {
      if (!techLayout) return;
      techLayout.classList.remove('is-limited');
      techLayout.classList.add('is-expanded-all');
      rows.forEach((row) => {
        row.hidden = false;
      });
      if (button) {
        button.hidden = true;
        button.setAttribute('aria-expanded', 'true');
      }
      requestAnimationFrame(configureAll);
    };
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tpdp-tech-show-more]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      expandAllTechRows(button);
    });
    let resizeFrame = 0;
    const scheduleConfigureAll = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(configureAll);
    };
    rows.forEach((row) => {
      const toggle = row.querySelector('[data-tpdp-tech-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', () => {
        if (!row.classList.contains('is-collapsible')) return;
        const expanded = row.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', String(expanded));
        const label = row.querySelector('dt')?.textContent?.trim().replace(/:$/, '') || 'esta informação';
        toggle.setAttribute(
          'aria-label',
          expanded ? `Mostrar menos em ${label}` : `Mostrar mais em ${label}`
        );
      });
    });
    requestAnimationFrame(configureAll);
    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleConfigureAll).catch(() => {});
    }
    const table = root.querySelector('.tpdp-tech__table');
    if ('ResizeObserver' in window && table) {
      let lastWidth = Math.round(table.getBoundingClientRect().width);
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const nextWidth = Math.round(entry.contentRect.width);
        if (nextWidth === lastWidth) return;
        lastWidth = nextWidth;
        scheduleConfigureAll();
      });
      observer.observe(table);
    } else {
      window.addEventListener('resize', scheduleConfigureAll, { passive: true });
    }
  };
  const initZoom = (root) => {
    if (root.dataset.zoomEnabled !== 'true') return;
    const modal = root.querySelector('[data-tpdp-zoom]');
    const modalImage = root.querySelector('[data-tpdp-zoom-image]');
    const canvas = root.querySelector('.tpdp-zoom__canvas');
    const openers = Array.from(root.querySelectorAll('[data-tpdp-zoom-open][data-zoom-src]'));
    if (!modal || !modalImage || !canvas || !openers.length) return;
    let currentIndex = 0;
    let lastFocused = null;
    let panX = 0;
    let panY = 0;
    let dragStartX = 0;
    let dragStartY = 0;
    let panStartX = 0;
    let panStartY = 0;
    let activePointerId = null;
    let didDrag = false;
    let suppressClick = false;
    const applyPan = () => {
      modalImage.style.setProperty('--tpdp-pan-x', `${panX}px`);
      modalImage.style.setProperty('--tpdp-pan-y', `${panY}px`);
    };
    const resetPan = () => {
      panX = 0;
      panY = 0;
      didDrag = false;
      suppressClick = false;
      modalImage.classList.remove('is-dragging');
      modalImage.style.removeProperty('--tpdp-pan-x');
      modalImage.style.removeProperty('--tpdp-pan-y');
    };
    const clampPan = (nextX, nextY) => {
      const imageRect = modalImage.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();
      const maxX = Math.max(0, (imageRect.width - canvasRect.width) / 2);
      const maxY = Math.max(0, (imageRect.height - canvasRect.height) / 2);
      return {
        x: Math.max(-maxX, Math.min(maxX, nextX)),
        y: Math.max(-maxY, Math.min(maxY, nextY))
      };
    };
    const showImage = (index) => {
      currentIndex = (index + openers.length) % openers.length;
      const opener = openers[currentIndex];
      resetPan();
      modalImage.classList.remove('is-zoomed');
      modalImage.src = opener.dataset.zoomSrc || opener.currentSrc || opener.src;
      modalImage.alt = opener.alt || '';
    };
    const open = (opener) => {
      const index = openers.indexOf(opener);
      currentIndex = index >= 0 ? index : 0;
      lastFocused = document.activeElement;
      showImage(currentIndex);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      modal.querySelector('[data-tpdp-zoom-close]:not(.tpdp-zoom__backdrop)')?.focus();
    };
    const close = () => {
      resetPan();
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      modalImage.src = '';
      modalImage.classList.remove('is-zoomed');
      document.documentElement.style.removeProperty('overflow');
      lastFocused?.focus?.();
    };
    openers.forEach((opener) => {
      opener.addEventListener('click', () => open(opener));
    });
    root.querySelectorAll('[data-tpdp-zoom-close]').forEach((button) => {
      button.addEventListener('click', close);
    });
    root.querySelector('[data-tpdp-zoom-prev]')?.addEventListener('click', () => showImage(currentIndex - 1));
    root.querySelector('[data-tpdp-zoom-next]')?.addEventListener('click', () => showImage(currentIndex + 1));
    modalImage.addEventListener('pointerdown', (event) => {
      if (!modalImage.classList.contains('is-zoomed')) return;
      activePointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      panStartX = panX;
      panStartY = panY;
      didDrag = false;
      modalImage.classList.add('is-dragging');
      modalImage.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    });
    modalImage.addEventListener('pointermove', (event) => {
      if (activePointerId !== event.pointerId) return;
      if (!modalImage.classList.contains('is-zoomed')) return;
      const deltaX = event.clientX - dragStartX;
      const deltaY = event.clientY - dragStartY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        didDrag = true;
      }
      const next = clampPan(panStartX + deltaX, panStartY + deltaY);
      panX = next.x;
      panY = next.y;
      applyPan();
      event.preventDefault();
    });
    const endDrag = (event) => {
      if (activePointerId !== event.pointerId) return;
      if (didDrag) suppressClick = true;
      modalImage.classList.remove('is-dragging');
      try {
        modalImage.releasePointerCapture?.(event.pointerId);
      } catch {
      }
      activePointerId = null;
    };
    modalImage.addEventListener('pointerup', endDrag);
    modalImage.addEventListener('pointercancel', endDrag);
    modalImage.addEventListener('click', () => {
      if (suppressClick) {
        suppressClick = false;
        return;
      }
      const willZoom = !modalImage.classList.contains('is-zoomed');
      resetPan();
      modalImage.classList.toggle('is-zoomed', willZoom);
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') showImage(currentIndex - 1);
      if (event.key === 'ArrowRight') showImage(currentIndex + 1);
    });
  };
  const initRoot = (root) => {
    if (!(root instanceof Element) || root.dataset.tpdpReady === 'true') return;
    root.dataset.tpdpReady = 'true';
    initTabs(root);
    initQuantity(root);
    initCart(root);
    initGallery(root);
    initTechRows(root);
    initZoom(root);
  };
  const init = (scope = document) => {
    if (scope instanceof Element && scope.matches(SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initRoot);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
